import * as L from 'leaflet'
import React, {useMemo} from 'react'
import {Marker, Popup} from 'react-leaflet'
import {BoxLink} from '../../../common/router_links/BoxLink'
import {slottsskogenPosition} from '../../../common/utils/slottsskogenPosition'
import {GenericMap, ZOOM_MANY_MARKERS} from '../../location/map/GenericMap'
import {BoxMarker} from './types/boxMarker'

interface Props {
  boxMarkers: BoxMarker[]
}

const INITIAL_CENTER_NUDGE = {lat: 0.8, long: 2}

const pinkActiveMarker = L.icon({
  iconUrl: '/markers/box-marker.svg',
  iconAnchor: [20, 50],
  popupAnchor: [0, -55],
})

const blackGreyActiveMarker = L.icon({
  iconUrl: '/markers/box-marker-blackgrey.svg',
  iconAnchor: [20, 50],
  popupAnchor: [0, -55],
})

const notActiveMarker = L.icon({
  iconUrl: '/markers/box-marker-grey.svg',
  iconAnchor: [20, 50],
})

// Toggle verbose map logs with: localStorage.setItem('mapDebug','1')
const MAP_DEBUG = typeof window !== 'undefined' && localStorage.getItem('mapDebug') === '1'

// We expect color to already be normalized to '#RRGGBB' upstream.
// Keep a defensive normalizer anyway.
function normalizeToHex6(input?: string | null): string | null {
  if (!input) return null
  const raw = String(input).trim()
  let m = raw.match(/^hex([0-9a-f]{6})$/i)
  if (m) return `#${m[1].toUpperCase()}`
  m = raw.match(/^#([0-9a-f]{6})$/i)
  if (m) return `#${m[1].toUpperCase()}`
  m = raw.match(/^([0-9a-f]{6})$/i)
  if (m) return `#${m[1].toUpperCase()}`
  return null
}

const isPink = (hex: string | null) => hex?.toLowerCase() === '#903373' || hex?.toLowerCase() === '#b40a7a'
const isDark = (hex: string | null) => hex?.toLowerCase() === '#2f3234'

function resolveBoxMarkerIcon(marker: BoxMarker) {
  if (!marker.active) return notActiveMarker

  // Normalize once, then reuse the same logic as the logger
  const hex = normalizeToHex6(marker.color)
  if (isPink(hex)) return pinkActiveMarker
  if (isDark(hex)) return blackGreyActiveMarker

  // default for unknown-but-active
  return blackGreyActiveMarker
}

export const BoxMap: React.FC<Props> = ({boxMarkers}) => {
  const markers = useMemo(
    () => boxMarkers.filter((m) => m.coordinates && typeof m.coordinates.lat === 'number' && typeof m.coordinates.long === 'number'),
    [boxMarkers],
  )

  if (MAP_DEBUG) {
    const counts: Record<string, number> = {}
    let nPink = 0,
      nDark = 0,
      nInactive = 0

    for (const m of markers) {
      const hex = normalizeToHex6(m.color) ?? String(m.color ?? 'null')
      counts[hex] = (counts[hex] || 0) + 1
      if (!m.active) nInactive++
      else if (isPink(hex)) nPink++
      else if (isDark(hex)) nDark++
    }
    console.log('[BoxMap] normalized color counts:', counts)
    console.log('[BoxMap] buckets -> pink:', nPink, 'dark:', nDark, 'inactive:', nInactive)
  }

  return (
    <GenericMap
      initialPosition={
        markers.length > 0
          ? [markers[0].coordinates.lat + INITIAL_CENTER_NUDGE.lat, markers[0].coordinates.long + INITIAL_CENTER_NUDGE.long]
          : slottsskogenPosition
      }
      zoom={ZOOM_MANY_MARKERS}
    >
      {markers.map((marker) => (
        <Marker icon={resolveBoxMarkerIcon(marker)} key={marker.id} position={[marker.coordinates.lat, marker.coordinates.long]}>
          <Popup>
            <BoxLink boxId={marker.id} linkText={marker.title} />
            {marker.locationName && <div>{marker.locationName}</div>}
            {marker.organisationName && <div>{marker.organisationName}</div>}
          </Popup>
        </Marker>
      ))}
    </GenericMap>
  )
}
